/**
 * CVD PANEL v2 — the pure half. R454 object 1, unblocked by R472(1).
 *
 * ⛔⛔ THE CONTRACT IS MEASURED, NOT DESCRIBED. gex2's header records what it cost to write a pane
 * against a contract someone described: three field names differed and the one that rotted silently
 * was the OPTIONAL one. So every field below was read off 600 live bars of `/api/es/cvd` at the
 * WORKER origin (2026-08-22 ~15:4xZ), not off a spec sentence:
 * ```
 *   t · instrument · cvd · delta · n_signed · n_unsigned · press_v1 · runner_up
 *   roll_pressure · excluded_spreads
 *   envelope: ok · root · cvd_state · study_status · session_reset_rule · age_s · budget_s · stale
 * ```
 * ⚠ AND THE ORIGIN MATTERS: `spx-hub.pages.dev/api/es/cvd` answers **HTTP 200 with the SPA shell**.
 * Fetch through `api.ts`'s `getJson`, never a bare fetch — *a 200 from the wrong origin is the most
 * expensive kind of success* (`api.ts` §, fourth instance today).
 *
 * ⛔ WHY v2 EXISTS, AND IT IS A SIGN ERROR NOT A STYLE CHANGE. v1 plots Pine DISPLAY space
 * (`press_v1 * MULT(150) - YPOS(2.5)`) and `MfWaveformPanel` draws its zero at display-0. Display-0
 * is `press_v1 = YPOS/MULT = 0.01667` — a *slightly positive* flow. The bipolar fill splits there,
 * so genuinely positive bars below that render NEGATIVE. Measured on the same 600 bars:
 * **14/600 = 2.3% of bars painted the wrong direction**, the offset being 4% of mean |flow|.
 * ⇒ ***SMALL IN MAGNITUDE, TOTAL IN MEANING: colour is the whole encoding of direction in that lane.***
 * v2 plots NATIVE units with a TRUE zero, so 0 means no flow.
 */

/** One bar exactly as `/api/es/cvd` lands it. `press_v1`/`delta` MAY be null — see `flowState`. */
export interface Cvd2Bar {
  t: number;
  instrument?: string | null;
  instrument_count?: number | null;
  ambiguous?: boolean | null;
  cvd?: number | null;
  /** Measured cumulative-delta OHLC. All-null marks a legacy pre-033 row; never infer from delta. */
  cvd_o?: number | null;
  cvd_h?: number | null;
  cvd_l?: number | null;
  delta?: number | null;
  press_v1?: number | null;
  n_signed?: number | null;
  n_unsigned?: number | null;
  src_bars?: number | null;
  signed_src_bars?: number | null;
  /** Server-owned session admission facts used by the verdict; never inferred from values. */
  session_key?: string | null;
  session_cvd_before?: number | null;
}

/** The served envelope. Optional fields are optional BECAUSE THE ROUTE MAY OMIT THEM. */
export interface Cvd2Feed {
  ok: boolean;
  root?: string;
  tf_applied?: string;
  bars?: Cvd2Bar[];
  cvd_state?: string | null;
  study_status?: string | null;
  session_reset_rule?: string | null;
  age_s?: number | null;
  budget_s?: number | null;
  stale?: boolean | null;
  /** Venue truth is served by the worker. The pane consumes it; it never carries a calendar. */
  session_verdict?: string | null;
  data_state?: string | null;
  next_open?: string | null;
  reason?: string | null;
  error?: string | null;
  contract_seam?: {
    state: "SAME" | "SEAM" | "UNMEASURED";
    gap_points?: number | null;
    spine_close?: number | null;
    tape_close?: number | null;
    minute?: string | null;
    tape_instrument_id?: string | null;
    ceiling_points?: number | null;
    reason?: string | null;
  };
}

/** CLOSED is the one venue override this feed may hand to the shared staleness chip. */
export function servedCvdVenueVerdict(feed: unknown): "CLOSED" | null {
  if (typeof feed !== "object" || feed === null || Array.isArray(feed)) return null;
  return (feed as { session_verdict?: unknown }).session_verdict === "CLOSED" ? "CLOSED" : null;
}

/**
 * ⛔⛔ FOUR STATES, AND `unknown` IS NOT `flat`. CC-ll amended his own spec §4a today on exactly this
 * class: ***when a missing value coerces to the most-preferred value, absence is not neutral — it
 * wins.*** In a BIPOLAR histogram the most-preferred position is **0 — the baseline itself**, so
 * `Number(null) === 0` would paint an UNMEASURED bar as a MEASURED flat one, sitting on the zero
 * line where the reader's eye rests.
 * ⚠ AND THE SAMPLE DOES NOT ACQUIT THE FIELD: my 600-bar window had 0 nulls and 2 measured zeros,
 * but `press_v1` is null by contract whenever `n_signed = 0`. ***A SAMPLE WITH NO NULLS PROVES THE
 * WINDOW WAS HEALTHY, NOT THAT THE FIELD IS NON-NULLABLE.*** Handled by contract, never by sample.
 */
export type FlowState = "up" | "down" | "flat" | "unknown";

export function flowState(delta: number | null | undefined): FlowState {
  if (delta === null || delta === undefined || !Number.isFinite(delta)) return "unknown";
  if (delta > 0) return "up";
  if (delta < 0) return "down";
  return "flat";
}

/**
 * The SHAPE for each state — a SECOND encoding channel, and it is not decoration.
 * `validate-palette` warns `--cvd-pos vs --up`: ΔE 16.3 with worst-under-colour-vision-simulation
 * **5.5 against a floor of 15**. It is forgiven only as *"CVD shortfall w/ declared secondary
 * encoding"* — so the declared secondary encoding has to actually EXIST.
 * ⇒ ***A PALETTE WARN THAT IS FORGIVEN BY A SECONDARY ENCODING BECOMES A FAIL THE DAY THE ENCODING
 * IS DROPPED.*** `unknown` is the one deliberate blank, and it is declared in the legend.
 */
export function flowMark(state: FlowState): string | null {
  switch (state) {
    case "up":
      return "▲";
    case "down":
      return "▼";
    case "flat":
      return "–";
    case "unknown":
      return null;
  }
}

/**
 * NATIVE units with a TRUE zero — the whole point of v2. No `MULT`, no `YPOS`.
 * A null stays null: an unsignable bar is NOT drawn, and is never averaged or plotted as 0.
 */
export function nativeMf(pressV1: number | null | undefined): number | null {
  return typeof pressV1 === "number" && Number.isFinite(pressV1) ? pressV1 : null;
}

/**
 * R1707 — SESSION is a display transform over the door's anchored spine, never a data mutation.
 * It may subtract only the exact session-open value, and the pane declares that transform.
 * CONT never calls this value into its arithmetic.
 *
 * Returns null unless a bar sits exactly at the session boundary. A prior bar is from another
 * session, and a first-loaded bar is page-local; neither may become the session-open anchor.
 */
export interface SessionAnchor {
  boundaryMs: number;
  /** The served cumulative at the boundary bar — what gets subtracted. */
  anchorValue: number;
  /** The exact session-open bar used; equal to boundaryMs by construction. */
  anchorBarT: number;
}

export function sessionAnchor(bars: Cvd2Bar[], boundaryMs: number): SessionAnchor | null {
  // ⛔ A NON-FINITE BOUNDARY IS NOT A BOUNDARY. Accepting NaN here would select an arbitrary bar and
  // then throw downstream in `toISOString()` — rendering a crash instead of a refusal (`codex review`).
  if (!Number.isFinite(boundaryMs)) return null;
  for (const b of bars) {
    if (!Number.isFinite(b.t) || b.t !== boundaryMs) continue;
    if (typeof b.cvd !== "number" || !Number.isFinite(b.cvd)) continue;
    return { boundaryMs, anchorValue: b.cvd, anchorBarT: b.t };
  }
  return null;
}

/** Whether the payload declares a valid server-side anchor; absence remains operator-visible. */
export type ServedAnchorState = "ANCHORED" | "UNANCHORED";

/**
 * ⛔⛔ ORDER 77 / R1303 — THE CLIENT ASSERTS THE CONVENTION, IT NO LONGER PERFORMS IT.
 *
 * The server now serves session-relative CVD (`cvdConvention.ts`), and says so by carrying
 * `session_key` on every bar. This function reads that claim; it never re-derives it.
 * ⚠ PRESENCE IS THE DISCRIMINATOR, and it has to be, because a re-based series and a continuous
 * one are both just numbers — nothing in the VALUES distinguishes "already anchored" from "raw".
 * A pane that guessed would double re-base on one guess and draw a 248k line on the other.
 */
export function servedAnchorState(bars: Cvd2Bar[]): {
  state: ServedAnchorState;
  sessionKey: string | null;
} {
  const keyed = bars.find((b) => typeof (b as { session_key?: unknown }).session_key === "string");
  const sessionKey = keyed ? String((keyed as { session_key?: unknown }).session_key) : null;
  /**
   * ⛔⛔ A KEY IS NOT AN ANCHOR (R1521 round 2, GK-bu at the line; Codex found it independently).
   * This read session_key PRESENCE alone. After ORDER 77's cure the server can legitimately serve
   * a session it KEYED but could not ANCHOR — base unknowable, session_cvd_before null, every
   * level null — and this returned ANCHORED over it. The pane then drew a flat zero line under a
   * confident pill: the pill asserted a convention the numbers did not carry.
   * ⇒ *** THE DISCRIMINATOR IS THE BASE, NOT THE LABEL. *** ANCHORED means "these levels are
   *   relative to a base the server could state", so the base must be present AND finite here.
   *   A NaN passes `!== null` and would re-open the same hole through the other door.
   */
  const anchoredBar = bars.find((b) => {
    const v = (b as { session_cvd_before?: unknown }).session_cvd_before;
    return typeof v === "number" && Number.isFinite(v);
  });
  return {
    state: sessionKey === null || anchoredBar === undefined ? "UNANCHORED" : "ANCHORED",
    sessionKey,
  };
}

/**
 * The cumulative as DRAWN.
 *
 * DESK-n53 R1707 gives the two display modes distinct, explicit semantics. CONT is identity on the
 * door's already-anchored spine; rebuilding it from deltas or subtracting any client offset is the
 * defect. SESSION may subtract exactly one value, and only when `sessionAnchor` found the exact
 * session-open bar. A gap remains a gap in either mode.
 */
export function anchoredCumulative(
  bars: Cvd2Bar[],
  anchor: SessionAnchor | null,
  mode: "continuous" | "session" = "continuous",
): { t: number; v: number | null }[] {
  const offset = mode === "session" && anchor !== null ? anchor.anchorValue : 0;
  return bars.map((b) => ({
    t: b.t,
    v: typeof b.cvd === "number" && Number.isFinite(b.cvd) ? b.cvd - offset : null,
  }));
}

/**
 * One visual mark for each consecutive-pair hole in a served fixed-width CVD series.
 *
 * ⛔ A CLOSED SESSION IS NOT A HOLE, AND TIME ALONE CANNOT TELL THEM APART. The first cut of this
 * function compared only `t` against `bucketMs`, so every overnight and weekend closure read as
 * missing data: measured on the built tree, S1 at t=0 followed by S2 at t=8h returned
 * `{t: 28800000, missingBuckets: 479}` — a 479-bucket MISSING DATA mark across a CME break the
 * venue was simply shut for. The box says one marker per REAL gap.
 *
 * ⭐ AND THE ARM THAT WAS SUPPOSED TO CATCH IT ASSERTED THE OUTCOME WITHOUT EXERCISING THE CODE:
 * its session-boundary fixture used two ADJACENT buckets, so it returned zero by CONTIGUITY and
 * never reached a session comparison at all — the `session_key` values in it were decorative. A
 * session-boundary arm is only an arm if its two bars are separated by TIME.
 *
 * So the producer's own admission fact decides. We skip a pair ONLY when both bars carry a
 * non-empty `session_key` and the keys differ: that is the server stating the session changed.
 * When either key is absent we cannot tell a closure from a hole, and we keep the marker — an
 * unexplained hole stays loud rather than being silently swallowed by a missing field.
 */
export function cvdGapMarkers(
  bars: Cvd2Bar[],
  bucketMs: number,
): Array<{ t: number; missingBuckets: number }> {
  const markers: Array<{ t: number; missingBuckets: number }> = [];
  for (let i = 1; i < bars.length; i += 1) {
    const previous = bars[i - 1]?.t;
    const current = bars[i]?.t;
    if (!Number.isFinite(previous) || !Number.isFinite(current)) continue;
    const missingBuckets = Math.floor((current - previous) / bucketMs) - 1;
    if (missingBuckets < 1) continue;
    const previousSession = bars[i - 1]?.session_key;
    const currentSession = bars[i]?.session_key;
    const bothStated = typeof previousSession === "string" && previousSession !== ""
      && typeof currentSession === "string" && currentSession !== "";
    if (bothStated && previousSession !== currentSession) continue;
    markers.push({ t: current, missingBuckets });
  }
  return markers;
}

/**
 * What the panel must SAY about the transform. Never null when an anchor is applied: the reader is
 * told the series was re-based, by how much, and against which rule the producer actually uses.
 */
export function anchorNote(feed: Cvd2Feed | null, anchor: SessionAnchor | null): string | null {
  if (!feed?.ok) return null;
  const rule = feed.session_reset_rule ?? "unstated";
  if (!anchor) {
    return rule === "continuous"
      ? `cumulative is CONTINUOUS as served (session_reset_rule=${rule}) — no anchor applied`
      : `no session anchor available (session_reset_rule=${rule}) — series drawn as served`;
  }
  const at = (ms: number) => new Date(ms).toISOString().slice(11, 16);
  return (
    `session anchor ${at(anchor.boundaryMs)}Z · DISPLAY ONLY: draw −${anchor.anchorValue} · ` +
    `source stays session_reset_rule=${rule}, NOT re-based.`
  );
}

/** One price bar as `/api/es/candles` lands it. The close is `c` — NOT `close`. */
export interface PriceBar {
  t: number;
  c?: number | null;
}

/**
 * ⛔⛔ THE INVARIANT THE PRODUCER GUARANTEES, EXPORTED SO A FIXTURE CAN BE HELD TO IT.
 *
 * `cvd` is the running sum of `delta`: measured **599/599 exactly** on the served window. Any bar
 * series where the cumulative step disagrees with its own delta IS NOT A PAYLOAD THIS ROUTE CAN EMIT.
 *
 * This exists because the FIRST version of `divergences()` compared the delta's sign against its own
 * cumulative's direction — **a quantity against itself.** It returned 0 on 600 live bars and its arm
 * was GREEN, because the fixture I wrote for it used `cvd` rising while `delta` was negative: a
 * state the producer cannot emit.
 * ⇒ ⭐⭐ ***AN ARM THAT PASSES ON A STATE THE PRODUCER CANNOT EMIT PROVES THE BRANCH EXECUTES, NOT
 * THAT THE FEATURE WORKS.*** rr's fixture law with the sign flipped: a consumer-authored fixture
 * cannot falsify the producer — and here it FABRICATED a capability the code did not have.
 * Had it shipped, the flag would have been permanently dark and read as "no divergences today".
 */
export function isCumulativeConsistent(bars: Cvd2Bar[]): boolean {
  for (let i = 1; i < bars.length; i++) {
    const prev = bars[i - 1].cvd;
    const now = bars[i].cvd;
    const d = bars[i].delta;
    if (typeof prev !== "number" || typeof now !== "number" || typeof d !== "number") continue;
    if (now - prev !== d) return false;
  }
  return true;
}

/**
 * DIVERGENCE — **PRICE** and **FLOW** disagreeing over the same bar. Price up while signed flow is
 * negative (or the reverse) is the classic CVD divergence and the only one that carries information.
 *
 * ⚠ IT IS EXPLICITLY NOT flow-vs-its-own-cumulative. See `isCumulativeConsistent` — that comparison
 * is a tautology and can never fire. Measured on the joined 600-bar window: **526 comparable bars,
 * 106 disagreements (20.2%)** for price-vs-flow, against **0** for the tautological form.
 *
 * ⚠ HYPOTHESIS, AND THE LABEL IS LAW 6, NOT A COURTESY. No pre-registered study has passed for this
 * flag, so it renders as HYPOTHESIS until one does, and it is a heads-up — never an action (law 5).
 * ⚠ AN UNKNOWN BAR CANNOT DIVERGE. A missing delta is an absence, not a disagreement; counting it
 * would manufacture signal out of a gap. A FLAT bar cannot diverge either, and neither can a bar
 * whose price did not move — there is no direction to contradict.
 */
export function divergences(bars: Cvd2Bar[], price: PriceBar[]): number[] {
  const closeAt = new Map<number, number>();
  for (const p of price) {
    if (typeof p.c === "number" && Number.isFinite(p.c)) closeAt.set(p.t, p.c);
  }
  const out: number[] = [];
  // ⛔⛔ ADJACENCY IS IN THE **BAR SERIES**, NOT IN THE JOINED SUBSET. A draft filtered to the joined
  // bars first and then compared neighbours — so a single missing price bar silently compared bar
  // i-2 against bar i, attributing a TWO-BAR price move to ONE bar's flow (`codex review`).
  // ⇒ ***A JOIN THAT DROPS ROWS MAKES NON-NEIGHBOURS ADJACENT, AND THE RESULT STILL LOOKS LIKE A
  // MEASUREMENT.*** This walks the cvd series in order and requires BOTH sides of the step to carry
  // a price: a gap yields no flag rather than a fabricated one.
  for (let i = 1; i < bars.length; i++) {
    const state = flowState(bars[i].delta);
    if (state === "unknown" || state === "flat") continue;
    const prevC = closeAt.get(bars[i - 1].t);
    const nowC = closeAt.get(bars[i].t);
    if (prevC === undefined || nowC === undefined) continue;
    const move = nowC - prevC;
    if (move === 0) continue; // no price direction to contradict
    if ((state === "up" && move < 0) || (state === "down" && move > 0)) out.push(bars[i].t);
  }
  return out;
}

/**
 * CVD CANDLES — R502(2): *"CVD drawn as delta-colored bars/candles (sign = color); the separate
 * blue/orange strip DIES."*
 *
 * ⭐ ONE ENCODING, NOT TWO. `cvd` is the running sum of `delta` (the invariant
 * `isCumulativeConsistent` guards), so a candle drawn from the PREVIOUS cumulative to THIS one is
 * delta-coloured **by construction**: it rises exactly when the delta is positive. Returning
 * candles rather than a parallel colour array means the colour cannot drift out of step with the
 * value it claims to describe — which is precisely how the retired blue/orange strip could
 * disagree with the lane above it.
 *
 * ⛔ A GAP BREAKS THE CHAIN. A null carries no cumulative, so the bar after a gap has no honest
 * `open`: spanning from the pre-gap value would draw ONE candle across the whole outage and colour
 * it by a step nobody observed. The chain restarts flat instead — `open === close` — which reads
 * as "resumed here", not as a move. (`divergences()` and `sessionFlips()` refuse the same way.)
 *
 * ⛔ NO INVENTED WICKS. `high`/`low` are the bounds of the step itself. The route serves bucketed
 * cumulatives, not an intrabar path, so a wick would assert a route the series never measured.
 */
export interface CvdCandle {
  t: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

export function cvdCandles(series: { t: number; v: number | null }[]): CvdCandle[] {
  const out: CvdCandle[] = [];
  let prev: number | null = null;
  let chained = false;
  for (const p of series) {
    const v = p.v;
    if (typeof v !== "number" || !Number.isFinite(v)) {
      chained = false; // the chain is broken; the next bar may not span the hole
      continue;
    }
    // ⛔⛔ A CHAIN START IS FLAT — INCLUDING THE FIRST BAR. GK-xx found this on the served route
    // (`40032d65`): the first candle opened on the ZERO LINE, so with a window starting 37h before
    // the session boundary it drew `0 -> -4834` — a FALLING body 26x the median, the largest on the
    // pane — while bar 0's delta was ** +21 **. It encoded 37 hours of pre-session accumulation as
    // one 12-minute step and coloured it against its own flow: v1's sign error, the exact class this
    // module's header says v2 exists to kill.
    // ⇒ ***THE ZERO LINE IS WHERE THE SESSION STARTS, NOT WHERE THE DATA STARTS.*** A first bar has
    // no previous cumulative, so it has no measured step — identical to a post-gap restart, and it
    // gets the identical rule.
    const open = chained && prev !== null ? prev : v;
    out.push({ t: p.t, open, high: Math.max(open, v), low: Math.min(open, v), close: v });
    prev = v;
    chained = true;
  }
  return out;
}

/**
 * SESSION FLIPS — R502(3)(a): *"session flip = CVD crossing the zero line -> glyph on the line + a
 * chip stating flip time/price."*
 *
 * ⭐ IT CONSUMES THE **ANCHORED** SERIES, NOT `bar.cvd`, AND THAT IS THE WHOLE POINT.
 * The zero line does not exist until the series is anchored to the session open — served cumulative
 * is continuous and may never cross zero at all while the DRAWN series crosses it repeatedly. A flip
 * is a property of what the owner SEES. Feeding this `bar.cvd` would report flips of a line that is
 * not on screen.
 *
 * ⛔ A GAP IS DISCLOSED, NEVER GUESSED AND NEVER SWALLOWED. When the two observed points either side
 * of a sign change are not adjacent in the series, the crossing INSTANT is unknown: we report the
 * flip at the first bar where the new sign is OBSERVED and set `acrossGap`, so the pane can say the
 * moment is unknown instead of drawing a glyph on a minute nobody measured. Hiding the flip would be
 * worse — the sign genuinely changed.
 *
 * ⛔ ZERO IS NOT A SIDE. Touching the line is not crossing it, and a zero does not clear the standing
 * sign: `+10, 0, +5` has not flipped. Treating 0 as a sign would emit two phantom flips per touch.
 *
 * ⛔ ADJACENCY IS IN THE **SERIES**, NOT THE JOINED SUBSET — `divergences()` learned this from a
 * `codex review` and the trap is identical here: price is a DECORATION on the flip, so a missing
 * close yields `price: null`, never a borrowed neighbour and never a suppressed flip.
 */
export interface SessionFlip {
  /** The first bar at which the NEW sign is observed. */
  t: number;
  /** `up` = crossed from below the zero line to above it. */
  direction: "up" | "down";
  /** Close at `t`, joined. `null` when the price series has no bar there — never invented. */
  price: number | null;
  /** True when the previous signed observation was not the adjacent series entry. */
  acrossGap: boolean;
}

export function sessionFlips(
  series: { t: number; v: number | null }[],
  price: PriceBar[],
): SessionFlip[] {
  const closeAt = new Map<number, number>();
  for (const p of price) {
    if (typeof p.c === "number" && Number.isFinite(p.c)) closeAt.set(p.t, p.c);
  }
  const out: SessionFlip[] = [];
  let lastSign = 0;
  let lastSignedIndex = -1;
  for (let i = 0; i < series.length; i++) {
    const v = series[i].v;
    if (typeof v !== "number" || !Number.isFinite(v)) continue; // a gap carries no sign
    const sign = v > 0 ? 1 : v < 0 ? -1 : 0;
    if (sign === 0) continue; // touching the line is not crossing it
    if (lastSign !== 0 && sign !== lastSign) {
      out.push({
        t: series[i].t,
        direction: sign > 0 ? "up" : "down",
        price: closeAt.get(series[i].t) ?? null,
        acrossGap: lastSignedIndex !== i - 1,
      });
    }
    lastSign = sign;
    lastSignedIndex = i;
  }
  return out;
}

/** The HYPOTHESIS/CALIBRATED chip, taken from the ROUTE — never asserted by the pane (law 6). */
export function studyChip(feed: Cvd2Feed | null): string | null {
  if (!feed?.ok) return null;
  const s = feed.study_status;
  return typeof s === "string" && s.length ? s : "HYPOTHESIS";
}

/**
 * Every un-drawable state names itself. A blank pane is the one outcome this function exists to
 * prevent — `null` means DRAWABLE, and nothing else does (gex2's G3, same shape, same reason).
 */
export function cvd2BlockedReason(
  feed: Cvd2Feed | null,
  error: string | null,
  expectRoot = "ES",
): string | null {
  if (error) return error;
  if (!feed) return null; // loading is not an error — the chip speaks, the pane does not shout
  if (!feed.ok) return feed.reason ?? feed.error ?? "the CVD route refused without naming a reason";
  if (!Array.isArray(feed.bars)) {
    return "the CVD route served no `bars` array — contract drift, refusing rather than throwing";
  }
  if (feed.bars.length === 0) {
    const namedUnread = typeof feed.reason === "string" && feed.reason.startsWith("LAST_SESSION_UNREAD");
    if ((servedCvdVenueVerdict(feed) === "CLOSED" && feed.data_state === "LAST_SESSION_UNREAD")
      || namedUnread) {
      return `EMPTY · ${feed.reason ?? "LAST_SESSION_UNREAD — the last complete session has no buckets"}`;
    }
    return "the CVD route served no bars for this window";
  }
  // ⛔⛔ LAW 2 — SPX ≠ SPXW, AND EVERY PANEL CARRIES ITS ROOT. A draft never checked `root`, so an
  // unidentified or wrong-root payload would render silently inside an ES panel (`codex review`).
  // ⇒ ***AN UNLABELLED FEED IS NOT A NEUTRAL FEED — it is a claim about a root, made by the panel's
  // frame rather than by the data.*** Refusing is cheap; a mislabelled flow lane is not.
  if (typeof feed.root !== "string" || feed.root.length === 0) {
    return "the CVD route served no `root` — this pane will not label another root's flow as ES";
  }
  if (feed.root !== expectRoot) {
    return `the CVD route served root=${feed.root}, this pane renders ${expectRoot} — refusing to pool roots`;
  }
  return null;
}

// ─── THE ADMISSION CONTRACT ──────────────────────────────────────────────────
/**
 * ⛔⛔ WHY A TYPE AND NOT A CONVENTION — GK-xx R526(2), 2026-08-23 12:14Z, MEASURED BY HIM:
 * ```
 *  NEW .svelte omitting `barSpanMs` (a TYPE) ................. 2 svelte-check ERRORS
 *  NEW .svelte rendering a Cvd2Feed it never checked,
 *    never calling cvd2BlockedReason ......................... NO ERROR
 * ```
 * ⇒ ***A PROPERTY HELD AS A TYPE SURVIVES A CHANGE OF VEHICLE; A PROPERTY HELD AS
 * A-FUNCTION-YOU-CALL DOES NOT.*** `cvd2BlockedReason` sat here armed by 11 assertions while the
 * LWC rewrite silently dropped its call site, and the suite stayed green — the compiler carried
 * the type across and carried NOTHING for the function.
 *
 * And GK-ww's sharpening of it: ***YOU DO NOT EVEN HAVE TO FORGET THE CALL. YOU CAN MAKE THE CALL
 * AND IGNORE ITS RETURN, AND A PRESENCE-ARM CANNOT TELL THE DIFFERENCE*** ("is it called?" and
 * "does its answer govern the render?" are different questions; only the second is the property).
 *
 * ⇒ SO THE PANE NO LONGER ACCEPTS BARS. It accepts a `DrawableCvd`, and the ONLY way to obtain
 * one is `admitCvd`, which mints it only when `cvd2BlockedReason` returned `null`. A surface that
 * skips the refusal has nothing of the right type to pass, and **the failure moves from a
 * runtime blank pane to a COMPILE ERROR.** A deletion mutant tests the call sites that EXIST;
 * this constrains the ones that do not exist YET.
 */
declare const DRAWABLE: unique symbol;

/**
 * CVD bars that have PASSED the refusal. `admitCvd` is the intended door.
 *
 * ⚠⚠ THE EXACT BOUND, MEASURED RATHER THAN ASSERTED — I first wrote "UNFORGEABLE" here and it is
 * FALSE. Two probes through `svelte-check`:
 * ```
 *   SKIPPER  <SessionCvdPane cvd={feed?.bars ?? []} />        ** ERROR ** — contract holds
 *            "Type 'Cvd2Bar[]' is missing ... bars, root, [DRAWABLE]"
 *   FORGER   { bars, root: "ES" } as DrawableCvd              ** COMPILES ** — assertion escapes
 * ```
 * ⇒ ***THIS TYPE STOPS THE ACCIDENT, NOT THE DELIBERATE CAST.*** A hand who has never heard of
 * `cvd2BlockedReason` CANNOT reach the pane — that is the whole failure mode we actually suffered
 * (the LWC rewrite dropped the call silently). A hand who writes `as DrawableCvd` has performed a
 * VISIBLE, GREPPABLE act, and `check:contract` fails on it outside this module.
 * ⚠ And no TS brand survives `as unknown as` — claiming otherwise would be the overclaim this
 * file exists to prevent. The guarantee is: **silence is impossible; only an explicit,
 * reviewable statement can bypass it.**
 */
export interface DrawableCvd {
  readonly [DRAWABLE]: true;
  readonly bars: Cvd2Bar[];
  /** Carried, not re-derived — LAW 2: the pane labels the root the DATA declared. */
  readonly root: string;
}

/** Exactly one of the two is non-null. A caller cannot read bars without handling the refusal. */
export type CvdAdmission =
  | { readonly drawable: DrawableCvd; readonly blocked: null }
  | { readonly drawable: null; readonly blocked: string };

/**
 * The ONLY door to a `DrawableCvd`.
 *
 * ⚠ IT DELEGATES, IT DOES NOT RE-DECIDE. Every refusal is still `cvd2BlockedReason`'s, so its 11
 * assertions remain the authority and this adds no second opinion — a pane that re-derives a
 * quantity is a second source of truth, which is the defect the retired blue/orange strip was.
 */
export function admitCvd(
  feed: Cvd2Feed | null,
  error: string | null,
  expectRoot = "ES",
): CvdAdmission {
  const blocked = cvd2BlockedReason(feed, error, expectRoot);
  if (blocked !== null) return { drawable: null, blocked };
  // cvd2BlockedReason returning null has ALREADY established bars is an array and root is a
  // non-empty string equal to expectRoot. The assertion below records that dependency.
  return {
    drawable: { bars: feed!.bars as Cvd2Bar[], root: feed!.root as string } as DrawableCvd,
    blocked: null,
  };
}
